import React, { useState, useEffect } from 'react';
// import ServicesButtonList from '../components/ServicesButtonList';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Schema from './Schema';
import ServiceNavigator from './ServiceNavigator';

const HealthReport = () => {
  const [showMoreIssues, setShowMoreIssues] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const commonIssues = [
    'Improper panel tilt, causing energy loss',
    'Damaged or loose MC4 connectors',
    'Absence of DC isolators or surge protection',
    'Underrated or non-compliant AC breakers',
    'Exposed cables with risk of electric shock',
    'Poor earthing and high fault current path',
  ];

  return (
    <div className="font-sans text-gray-900 bg-gray-100">
    <Schema />
      {/* Hero Section with Black Background */}
      <section
        className="flex flex-col items-center gap-8 mt-16 mb-16 bg-black text-white rounded-3xl overflow-hidden mx-4 sm:mx-auto py-20 max-w-6xl text-center"
        data-aos="fade-up"
      >
        <div className="w-full max-w-3xl">
          <h2 className="text-5xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Solar System Health Report
          </h2>
          <p className="text-xl sm:text-2xl text-gray-300 mb-4 italic">
            Certified Inspection & Fire Safety Audit for Solar Installations in Sri Lanka
          </p>
        </div>
      </section>

      {/* Intro Paragraph */}
      <section
        className="px-6 max-w-4xl mx-auto text-gray-700 text-lg sm:text-xl leading-relaxed mb-14"
        data-aos="fade-up"
      >
        <p>
          Ensure your solar power system is safe, efficient, and fully compliant with the latest regulations.
          Our <strong>Solar System Health Report</strong> is a detailed on-site inspection service carried out by certified professionals,
          focused on <strong>SLSEA compliance</strong>, <strong>performance analysis</strong>, and <strong>fire hazard detection</strong> for
          residential and commercial solar systems.
        </p>
      </section>

      {/* Changed border color from yellow-400 to black */}
      {/* <hr className="border-black max-w-4xl mx-auto my-12" /> */}

      {/* What We Offer Section */}
      <section
        className="px-6 max-w-4xl mx-auto mb-20"
        data-aos="fade-right"
      >
        {/* Changed text color from yellow-600 to black */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-black text-center">What We Offer</h2>

        {[
          {
            title: 'Full Technical Evaluation',
            points: [
              'Inspection of solar panels, inverters, DC/AC cabling, isolators, breakers, and mounting structures.',
              'System tested against Sustainable Energy Authority (SLSEA) of Sri Lanka guidelines.',
            ],
          },
          {
            title: 'Fire & Electrical Safety Audit',
            points: [
              'Identify hotspots, loose connections, and faulty breakers.',
              'Check insulation resistance, grounding, surge protection (SPD), and ELCBs.',
              'Prevent system-related fire risks and electrical hazards.',
            ],
          },
          {
            title: 'Performance Degradation Assessment',
            points: [
              'Evaluate energy losses due to shading, wrong tilt/orientation, soiling, and faulty components.',
              'Real-time inverter log and MPPT data analysis.',
            ],
          },
          {
            title: 'Detailed Compliance Report',
            points: [
              'Clear summary of faults with images.',
              'Breakdown of SEA standard violations.',
              'Actionable repair and upgrade recommendations.',
            ],
          },
          {
            title: 'Advanced Testing (Optional)',
            points: [
              'Thermal imaging for panel and isolator temperature mapping.',
              'Drone inspection for hard-to-reach rooftops.',
              'Earth resistance and string current testing.',
            ],
          },
        ].map(({ title, points }, i) => (
          <article key={i} className="mb-10">
            {/* Changed from yellow-700 to black */}
            <h3 className="text-xl font-semibold mb-4 text-black">{title}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 text-base">
              {points.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </article>
        ))}
      </section>

      {/* Changed border color from yellow-400 to black */}
      {/* <hr className="border-black max-w-4xl mx-auto my-12" /> */}

      {/* Common Issues */}
      <section
        className="px-6 max-w-4xl mx-auto mb-20"
        data-aos="fade-left"
      >
        {/* Changed text color from yellow-600 to black */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-black text-center">Common Issues We Detect</h2>
        <ul
          className="list-disc list-inside text-gray-700 space-y-2 max-w-xl mx-auto text-base"
          id="common-issues-list"
        >
          {(showMoreIssues ? commonIssues : commonIssues.slice(0, 3)).map((issue, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="text-red-600 font-bold">•</span> {issue}
            </li>
          ))}
        </ul>
        <div className="text-center mt-6">
          <button
            onClick={() => setShowMoreIssues(!showMoreIssues)}
            className="text-black hover:underline font-semibold focus:outline-none"
            aria-expanded={showMoreIssues}
            aria-controls="common-issues-list"
          >
            {showMoreIssues ? 'See less...' : 'See more...'}
          </button>
        </div>
      </section>

      {/* Changed border color from yellow-400 to black */}
      {/* <hr className="border-black max-w-4xl mx-auto my-12" /> */}

      {/* Who Should Get Report */}
      <section
        className="px-6 max-w-4xl mx-auto mb-20"
        data-aos="fade-up"
      >
        {/* Changed text color from yellow-600 to black */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-black text-center">Who Should Get a Health Report?</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-base text-black leading-relaxed w-full">
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Homeowners concerned about safety and performance</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Commercial properties needing compliance for insurance</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Systems with missing or unresponsive installers</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Any solar user wanting to prevent fire risks and extend system lifespan</li>
        </ul>
      </section>

      {/* Changed border color from yellow-400 to black */}
      {/* <hr className="border-black max-w-4xl mx-auto my-12" /> */}

      {/* Final Report Includes */}
      <section
        className="px-6 max-w-4xl mx-auto mb-20"
        data-aos="fade-up"
      >
        {/* Changed text color from yellow-600 to black */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-black text-center">Final Report Includes</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-base text-black leading-relaxed w-full">
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Full Compliance Checklist (SEA standards)</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Issue & Risk Summary (with photos)</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Estimated Energy Loss (%)</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Fire Hazard Indicators</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Rectification Plan with Recommendations</li>
        </ul>
      </section>

      {/* Changed border color from yellow-400 to black */}
      {/* <hr className="border-black max-w-4xl mx-auto my-12" /> */}

      {/* Why Choose Us */}
      <section
        className="px-6 max-w-4xl mx-auto mb-28"
        data-aos="fade-up"
      >
        {/* Changed text color from yellow-600 to black */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-black text-center">Why Choose Us?</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-base text-black leading-relaxed w-full">
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Experts with 10+ Years of Experience in Solar Installations</li>
         <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Certified SEA-Compliant Engineers</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Over 44,000 kW of Systems Assessed</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Fast Turnaround with On-Site & Digital Report Delivery</li>
          <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">Support for All Brands & Systems (Domestic & Commercial)</li>
        </ul>
      </section>

      {/* Contact Section with Black Background */}
      <section
        className="flex flex-col items-center gap-8 mt-16 mb-16 bg-black text-white rounded-3xl overflow-hidden mx-4 sm:mx-auto py-20 max-w-6xl text-center"
        data-aos="fade-up"
      >
        <div className="w-full max-w-2xl">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8">
            Book Your Health Report Service
          </h2>
          <div className="w-full max-w-2xl">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8">
            Get in Touch
          </h2>
          <p className="mb-2 font-medium">Solar Care International Pvt Ltd</p>
          <p className="mb-1">No.44/1A, Chapel Lane, Nugegoda, Sri Lanka</p>
          <p className="mb-1">+94 114 199 225 / +94 777 129 021</p>
          <p>
            <a
              href="mailto:info@solarcareinternational.com"
              className="text-blue-400 hover:text-blue-200 underline transition"
            >
              info@solarcareinternational.com
            </a>
          </p>
        </div>
        </div>
      </section>

      {/* <ServicesButtonList /> */}
      <ServiceNavigator />
    </div>
  );
};

export default HealthReport;